import type { CompetitorRecord } from "@/lib/compare/types";

const PRICING = "https://clipchamp.com/en/pricing/";
const M365_PRICING = "https://www.microsoft.com/en-us/microsoft-365/buy/compare-all-microsoft-365-products";
const SUB_CHANGES = "https://support.microsoft.com/en-us/clipchamp/changes-to-clipchamp-subscriptions";
const STORAGE_FAQ =
  "https://support.microsoft.com/en-us/Clipchamp/clipchamp-cloud-storage-frequently-asked-questions";
const GAMING = "https://clipchamp.com/en/gaming-video-editor/";
const FEATURES = "https://clipchamp.com/en/features/";
const HOME = "https://clipchamp.com/en/";
const WIKI = "https://en.wikipedia.org/wiki/Clipchamp";
const CHECKED = "2026-10-08";

export const CLIPCHAMP: CompetitorRecord = {
  slug: "clipchamp",
  name: "Clipchamp",
  aliases: ["Microsoft Clipchamp", "Clip Champ", "clipchamp.com"],
  url: "https://clipchamp.com",
  pricingUrl: PRICING,
  category: "editor",
  positioning:
    "Record, edit, and share HD videos online using AI video editing tools, no expertise required.",
  plans: [
    {
      name: "Free",
      monthly: 0,
      metered: "Nothing is metered: unlimited exports, no credits, no minutes",
      includes: [
        "Personal Microsoft account required",
        "Up to 1080p export, watermark-free",
        "Free stock media and filters",
        "AI tools included",
        "5 GB cloud storage",
        "No brand kit",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
      note: "Storage figure from the Clipchamp cloud storage FAQ.",
    },
    {
      name: "Premium via Microsoft 365 Personal",
      monthly: 9.99,
      annualPerMonth: 8.33,
      annualTotal: 99.99,
      metered: "Nothing is metered: unlimited exports, no credits, no minutes",
      includes: [
        "Up to 4K UHD export",
        "Premium stock assets, filters and effects",
        "Single brand kit",
        "1 TB OneDrive storage",
        "The rest of Microsoft 365 Personal",
      ],
      sourceUrl: M365_PRICING,
      checkedAt: CHECKED,
      note:
        "The Clipchamp pricing page prints no dollar amount; Premium is included with Microsoft 365 Personal ($9.99/mo or $99.99/yr) and Family ($12.99/mo or $129.99/yr), priced on microsoft.com. Legacy standalone Premium plans were retired and a standalone price is unverified.",
    },
    {
      name: "Work",
      custom: true,
      metered: "Nothing is metered",
      includes: [
        "Standard features in some Microsoft 365 business and education licenses",
        "Full premium and AI video creation need a Copilot license",
        "Billed per Microsoft 365 tenant, not per creator",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
      note: "Price depends on the organisation's Microsoft 365 licence.",
    },
  ],
  entryPlanName: "Premium via Microsoft 365 Personal",
  entryPlanIncludes:
    "4K export, premium stock and effects, one brand kit and 1 TB OneDrive, bundled with Microsoft 365 Personal",
  freeTier:
    "Free with a personal Microsoft account: unlimited watermark-free exports up to 1080p, free stock, AI tools and 5 GB storage",
  trial: "None needed; the Free plan has no expiry and no watermark",
  meteringUnit: "None. No credits, no minutes, no export cap on any tier",
  exportResolution: "1080p on Free, 4K UHD with Microsoft 365 Personal or Family",
  features: {
    longToShort: { v: "no", note: "Auto highlights: Not present", source: GAMING },
    momentRanking: { v: "no", note: "No virality ranking", source: GAMING },
    reframing: { v: "no", note: "Manual crop and aspect presets only", source: FEATURES },
    maxSource: { v: "text", note: "4 GB per import, no length cap published", source: GAMING },
    layouts: { v: "n/a", note: "Aspect presets only, no clip layouts", source: FEATURES },
    redditStory: { v: "no" },
    fakeText: { v: "no" },
    aiImages: { v: "no", note: "Not on the features page", source: FEATURES },
    voiceover: { v: "yes", note: "Text-to-speech, adjustable pitch and pace", source: FEATURES },
    voiceCloning: { v: "n/a", note: "Not on the features page" },
    avatars: { v: "n/a", note: "Not on the features page" },
    timelineEditor: { v: "yes", note: "Multi-track, on Free", source: FEATURES },
    captionStyles: { v: "partial", note: "Animated titles, no short-form presets", source: HOME },
    captionLanguages: { v: "text", note: "80+ languages", source: HOME },
    bRoll: { v: "partial", note: "Stock library, no AI B-roll", source: PRICING },
    fourK: { v: "partial", note: "Microsoft 365 Personal or Family only", source: PRICING },
    scheduling: { v: "partial", note: "Share to YouTube, TikTok, LinkedIn; no scheduler", source: FEATURES },
    analytics: { v: "no" },
    socialTracker: { v: "no" },
    api: { v: "no", note: "No public API" },
    webhooks: { v: "no" },
    web: { v: "yes" },
    desktop: { v: "yes", note: "Windows app, preinstalled on Windows 11", source: WIKI },
    mobile: { v: "no", note: "iOS app ended June 2026, no Android", source: WIKI },
    teamSeats: { v: "no", note: "Work version is per Microsoft 365 tenant", source: PRICING },
    freeTier: { v: "yes", note: "1080p, watermark-free, unlimited exports", source: PRICING },
    trial: { v: "no", note: "Free tier instead" },
    metering: { v: "text", note: "None" },
    rollover: { v: "n/a", note: "Nothing is metered" },
    refunds: { v: "text", note: "Not stated on the pricing page" },
    cancelInApp: { v: "n/a", note: "Not stated on the pricing page" },
  },
  billingTerms: {
    refunds: "No refund terms appear on the Clipchamp pricing page as of Oct 8, 2026.",
    cancel:
      "Cancellation steps are not described on the Clipchamp pricing page. Premium is tied to a Microsoft 365 subscription managed in the Microsoft account.",
    sourceUrl: PRICING,
    checkedAt: CHECKED,
  },
  quotes: [],
  verification: {
    method: "fetch",
    date: CHECKED,
    note:
      "Clipchamp pricing page fetched directly: Free tier limits and the Premium feature list come from it, but it prints no dollar amount. The $9.99/mo and $99.99/yr Premium figures are the Microsoft 365 Personal subscription price from microsoft.com (Family $12.99/mo or $129.99/yr); the $8.33 annual-per-month is computed. The 5 GB and 1 TB storage figures come from the Clipchamp cloud storage FAQ. Legacy standalone Premium plans were retired per the subscription changes help page; third-party sites list a standalone $11.99/mo, which is unverified and not used.",
  },
  seoPattern: "No /vs/, /compare/ or /alternatives/ URLs in the sitemap, no page about Crayo",
};
